// Seiten der Office-SPA: welche Permission eine Seite verlangt und unter welchem
// Menüpunkt (NAV_TREE) sie hängt. Routen-Guard und Sidebar lesen dieselbe Tabelle —
// eine Seite ohne Eintrag ist für niemanden erreichbar (Default Deny).
//
// Die Permission einer Seite ist die ihrer ersten Daten-Anfrage (Route-Inventar
// docs/rbac/route-permissions.csv). Das ist Ausblenden, keine Sicherheitsgrenze:
// die Daten schützt das Backend.

import { P, type PermissionKey } from './permissions.generated';

export interface PageAccess {
  /** Beschriftung in der Sidebar; ohne Beschriftung erscheint die Seite nicht im Menü. */
  label?: string;
  /** Menüpunkte (NAV_TREE-ids), unter denen die Seite verlinkt wird. */
  nav?: readonly string[];
  /**
   * Zwischenüberschrift im aufgeklappten Menüpunkt. Nur Darstellung — ein langer
   * Punkt wie „Website & CMS“ bleibt sonst eine Liste ohne Halt fürs Auge.
   */
  gruppe?: string;
  /** Mindestens einer der Keys; eine leere Liste lässt niemanden durch. */
  anyOf: readonly PermissionKey[];
}

const PERSON = [P.personen.personen.person_ansehen];
const PROFIL = [P.personen.profile.profile_ansehen];
const LISTEN = [P.personenlisten.personenlisten_ansehen];
const CMS = [P.cms.inhalte.inhalte_ansehen];
const MIGRATION = [P.admin.migration_und_daten.migrationskonfiguration_ansehen];
const REGULARIEN = [P.standort.vertragsregularien.vertragsregularien_ansehen];
const OEFFENTLICH = [P.admin.oeffentliche_bereiche.oeffentliche_bereiche_ansehen];
const SYSTEM = [P.admin.system_und_schnittstellen.systemeinstellungen_ansehen];
const VERKAUF = [P.admin.verkaufsaktionen.gutscheine_und_rabattcodes_ansehen];
// Tickets (docs/testing-tickets-plan.md), dauerhaft.
const TICKETS = [P.testing.tickets.tickets_ansehen];
// Abnahme-Katalog (docs/abnahme-plan.md).
const ABNAHME = [P.abnahme.katalog.katalog_ansehen];
const STANDORTE = [P.admin.standorte.standorte_ansehen, P.standort.standort_stammdaten.standortdaten_ansehen];

const cms = (label: string, gruppe?: string): PageAccess => ({ label, nav: ['website'], anyOf: CMS, ...(gruppe ? { gruppe } : {}) });

export const PAGES: Record<string, PageAccess> = {
  // Dashboard
  '/dashboard': { label: 'Übersicht', nav: ['dashboard'], anyOf: [P.dashboard.dashboard_ansehen] },
  '/system': { label: 'Unternehmensübersicht', nav: ['dashboard'], anyOf: [P.dashboard.unternehmenskennzahlen] },

  // Arbeitsbereiche
  '/dashboard/todo': { label: 'Aufgaben', nav: ['aufgaben'], anyOf: [P.arbeit.aufgaben.aufgaben_ansehen] },
  '/dashboard/kalender': {
    label: 'Kalender',
    nav: ['kalender'],
    anyOf: [P.arbeit.kalendereintraege.team_eintraege_ansehen],
  },
  '/dashboard/trial-requests': { label: 'Anfragen', nav: ['anfragen'], anyOf: [P.anfragen.anfragen_ansehen] },
  '/dashboard/trials': { label: 'Anmeldung & Start', nav: ['anmeldung'], anyOf: [P.anmeldung.eintrag_ansehen] },
  '/dashboard/kuendigungen': { label: 'Kündigungen', nav: ['kuendigungen'], anyOf: [P.anmeldung.kuendigungen_ansehen] },

  // Personen
  '/dashboard/people': { label: 'Personen', nav: ['personen'], anyOf: PERSON },
  '/dashboard/people/:id': { anyOf: PERSON },
  '/dashboard/people/:id/sub/:subId': { anyOf: PROFIL },
  '/dashboard/search': { anyOf: PERSON },
  '/dashboard/person-lists': { label: 'Personenlisten', nav: ['personenlisten'], anyOf: LISTEN },
  // Kontextrouten der Admin-Suche (gleiche Seiten, anderer Pfad)
  '/system/dashboard/people': { anyOf: PERSON },
  '/system/dashboard/people/:id': { anyOf: PERSON },
  '/system/dashboard/people/:id/sub/:subId': { anyOf: PROFIL },
  '/system/dashboard/person-lists': { anyOf: LISTEN },
  '/system/dashboard/search': { anyOf: PERSON },

  // Unterricht
  '/dashboard/schedule': { label: 'Terminübersicht', nav: ['termine'], anyOf: [P.termine.eintraege_ansehen] },
  '/dashboard/lehrerwechsel': { label: 'Lehrerwechsel', nav: ['lehrerwechsel'], anyOf: [P.termine.eintraege_ansehen] },
  '/dashboard/rooms': { label: 'Raumplan', nav: ['raumplan'], anyOf: [P.raumplan.raeume_ansehen] },

  // Finanzen
  '/dashboard/accounting/payments': {
    label: 'Zahlungen',
    nav: ['finanzen'],
    anyOf: [P.buchhaltung.finanzdaten_und_zahlungen.zahlungen_ansehen],
  },
  // Zahlungsläufe (ADR-0003): Guard wie GET /billing/payment-runs.
  '/dashboard/accounting/payment-runs': {
    label: 'Zahlungsläufe',
    nav: ['finanzen'],
    anyOf: [P.buchhaltung.finanzdaten_und_zahlungen.zahlungen_ansehen],
  },
  // Kontoauszüge (ADR-0003 Phase 3): Guard wie GET /billing/bank-statements.
  '/dashboard/accounting/bank-statements': {
    label: 'Kontoauszüge',
    nav: ['finanzen'],
    anyOf: [P.buchhaltung.finanzdaten_und_zahlungen.zahlungen_ansehen],
  },
  // Mahnwesen (ADR-0003 Phase 7): Guard wie GET /billing/dunning-notices.
  '/dashboard/accounting/dunning': {
    label: 'Mahnwesen',
    nav: ['finanzen'],
    anyOf: [P.buchhaltung.ruecklastschriften_und_mahnwesen.mahnwesen_ansehen],
  },
  // DATEV-Export (ADR-0003 Phase 7): Guard wie GET /billing/datev/export.
  '/dashboard/accounting/datev': {
    label: 'DATEV-Export',
    nav: ['finanzen'],
    anyOf: [P.buchhaltung.buchhaltungsbelege_und_dokumente.finanzdokumente_ansehen],
  },
  // Liquiditätsprognose (services/cashflow_prognose.py): Guard wie GET /payments/cashflow-forecast.
  '/dashboard/accounting/liquiditaet': {
    label: 'Liquiditätsprognose',
    nav: ['finanzen'],
    anyOf: [P.buchhaltung.finanzdaten_und_zahlungen.liquiditaetsprognose_ansehen],
  },
  // Lehrer-Rechnungen: Guard wie die API (GET /teacher-invoices, finanzdokumente_ansehen).
  '/system/finance/payouts': {
    label: 'Lehrer-Auszahlungen',
    nav: ['finanzen'],
    anyOf: [P.buchhaltung.buchhaltungsbelege_und_dokumente.finanzdokumente_ansehen],
  },

  // Recruiting
  '/standort/bewerbungen': { label: 'Bewerbungen', nav: ['recruiting'], anyOf: [P.recruiting.bewerbungen.bewerbungen_ansehen] },
  // Entscheidung F02 (2026-09-21): Stellenangebote pflegt Recruiting mit den eigenen Rechten.
  '/standort/jobs': { label: 'Stellenangebote', nav: ['recruiting'], anyOf: [P.recruiting.jobangebote.jobangebote_ansehen] },

  // Marketing
  '/system/marketing': {
    label: 'Marketing',
    nav: ['marketing'],
    anyOf: [
      P.marketing.marketing_integrationen.marketing_integrationen_ansehen,
      P.marketing.marketing_auswertungen.marketing_auswertungen_ansehen,
    ],
  },

  // Website & CMS — in der Reihenfolge der Sidebar, gruppiert nach dem, was man
  // dort sucht: eine Seite der Website, ein Inhalt auf mehreren Seiten, der Rahmen
  // um alle Seiten, der Blog.
  '/system/cms': cms('Übersicht'),
  '/system/cms/home': cms('Startseite', 'Seiten'),
  '/system/cms/ueber-uns': cms('Über uns', 'Seiten'),
  '/system/cms/standorte': cms('Standorte', 'Seiten'),
  '/system/cms/faecher': cms('Fächer', 'Seiten'),
  '/system/cms/jobs': cms('Jobs', 'Seiten'),
  '/system/cms/familycard': cms('FamilyCard', 'Seiten'),
  '/system/cms/seiten': cms('Eigene Seiten', 'Seiten'),
  '/system/cms/landingpages': cms('Landingpages', 'Seiten'),
  // ThunderBuilder: das Übergabe-Token verlangt Bearbeiten, nicht nur Ansehen.
  '/system/cms/builder': { label: 'Seiten gestalten', nav: ['website'], anyOf: [P.cms.inhalte.inhalte_bearbeiten], gruppe: 'Seiten' },
  '/system/cms/team': cms('Team', 'Inhalte'),
  '/system/cms/faq': cms('FAQ', 'Inhalte'),
  '/system/cms/partners': cms('FamilyCard-Partner', 'Inhalte'),
  '/system/cms/news': cms('News', 'Inhalte'),
  '/standort/portal-content': cms('News am Standort', 'Inhalte'),
  '/system/cms/media': cms('Medien', 'Inhalte'),
  '/system/cms/navigation': cms('Navigation', 'Aufbau & Recht'),
  '/system/cms/footer': cms('Footer', 'Aufbau & Recht'),
  '/system/cms/rechtstexte': cms('Rechtstexte', 'Aufbau & Recht'),
  '/system/cms/cookie': cms('Cookie-Banner', 'Aufbau & Recht'),
  // Design der ganzen Website ändert nur die Gestaltung (Builder-Rolle „gestaltung“).
  '/system/cms/design': {
    label: 'Design',
    nav: ['website'],
    anyOf: [P.cms.website_struktur.website_struktur_bearbeiten],
    gruppe: 'Aufbau & Recht',
  },
  '/system/cms/redaktion': cms('Redaktion', 'Blog & Social'),
  '/system/cms/social': cms('Social', 'Blog & Social'),

  // Auswertungen
  '/system/reports': {
    label: 'Berichte',
    nav: ['auswertungen'],
    anyOf: [P.auswertungen.standard_und_individuelle_auswertungen.auswertungen_ansehen],
  },
  '/dashboard/ai-chat': {
    label: 'KI-Assistent',
    nav: ['auswertungen'],
    anyOf: [P.auswertungen.ki_gestuetzte_auswertungen.ki_auswertungen_verwenden],
  },

  // Administration: Standort
  '/system/organization/tenants': { label: 'Standorte', nav: ['standort-stammdaten', 'admin-standorte'], anyOf: STANDORTE },
  '/system/organization/tenants/new': { anyOf: [P.admin.standorte.standorte_ansehen] },
  '/system/organization/tenants/:id': { anyOf: STANDORTE },
  '/standort/master-data': {
    label: 'Fächer & Unterrichtsformen',
    nav: ['standort-angebot', 'standort-formen', 'admin-angebote'],
    anyOf: [
      P.standort.unterrichtsangebot.unterrichtsangebot_ansehen,
      P.standort.unterrichtsformen_und_pakete.unterrichtsformen_ansehen,
      P.admin.globale_angebote.globale_angebote_ansehen,
    ],
  },
  // Entscheidung F08 (2026-09-21): Der Standort pflegt Abweichungen zum globalen Angebot.
  '/standort/angebot': {
    label: 'Angebot am Standort',
    nav: ['standort-formen'],
    anyOf: [P.standort.unterrichtsformen_und_pakete.unterrichtsformen_ansehen],
  },
  // Tarife (ADR-0003): Guard wie GET /billing/tariffs.
  '/standort/tariffs': {
    label: 'Tarife',
    nav: ['standort-formen', 'admin-angebote'],
    anyOf: [P.standort.unterrichtsformen_und_pakete.pakete_ansehen],
  },
  '/standort/rooms': {
    label: 'Räume & Tags',
    nav: ['standort-raeume'],
    anyOf: [P.standort.raeume_und_ausstattung.raumkonfiguration_ansehen],
  },
  '/standort/rules/kuendigungsfristen': { label: 'Kündigungsfristen', nav: ['standort-regularien'], anyOf: REGULARIEN },
  '/standort/rules/unterrichtsfreie-zeiten': { label: 'Unterrichtsfreie Zeiten', nav: ['standort-regularien'], anyOf: REGULARIEN },
  '/standort/rules/gebuehren': { label: 'Gebühren', nav: ['standort-regularien'], anyOf: REGULARIEN },
  '/standort/users': {
    label: 'Benutzer am Standort',
    nav: ['standort-benutzer'],
    anyOf: [P.standort.benutzer_und_zugriffsrechte.standortbenutzer_ansehen],
  },

  // Administration: global
  '/system/organization/users': {
    label: 'Benutzer & Rollen',
    nav: ['admin-benutzer'],
    anyOf: [P.admin.benutzer_und_rollen.benutzer_ansehen],
  },
  '/system/organization/security': {
    label: 'MFA & Sicherheit',
    nav: ['admin-sicherheit'],
    anyOf: [P.admin.sicherheit.sicherheitseinstellungen_ansehen],
  },
  // Die Fallbearbeitung ist Büroarbeit wie die Anmeldung (Bestandskunde zieht um); Import,
  // Mappings und Reset bleiben unter „Migration & Daten“ dem Super-Admin vorbehalten.
  '/dashboard/migration-cases': { label: 'Offene Migrationen', nav: ['migrationen'], anyOf: [P.anmeldung.eintrag_ansehen] },
  '/standort/migration': { label: 'Migration', nav: ['admin-migration'], anyOf: MIGRATION },
  '/standort/migration/fach-mappings': { label: 'Fach-Mappings', nav: ['admin-migration'], anyOf: MIGRATION },
  '/standort/migration/uform-mappings': { label: 'Unterrichtsform-Mappings', nav: ['admin-migration'], anyOf: MIGRATION },
  '/system/finance/stripe': {
    label: 'Zahlungsanbieter',
    nav: ['admin-zahlungswesen'],
    anyOf: [P.admin.zahlungswesen_und_integrationen.zahlungsintegration_ansehen],
  },
  // Abrechnungsmodell und abrechnende Einheiten (ADR-0003): Guard wie GET /billing/settings.
  '/system/finance/billing': {
    label: 'Abrechnung',
    nav: ['admin-zahlungswesen'],
    anyOf: [P.admin.zahlungswesen_und_integrationen.zahlungsintegration_ansehen],
  },
  '/system/communication/templates': { label: 'Vorlagen', nav: ['admin-vorlagen', 'standort-vorlagen'], anyOf: [P.admin.vorlagen.vorlagen_ansehen] },
  '/system/platform/features': { label: 'Funktionen', nav: ['admin-oeffentlich'], anyOf: OEFFENTLICH },
  '/system/communication/chat': { label: 'Chat-Konversationen', nav: ['admin-oeffentlich'], anyOf: OEFFENTLICH },
  '/system/communication/email': {
    label: 'E-Mail',
    nav: ['admin-system'],
    anyOf: [P.admin.system_und_schnittstellen.api_schnittstellen_ansehen],
  },
  '/system/communication/settings': { label: 'Kommunikation', nav: ['admin-system'], anyOf: SYSTEM },
  '/system/platform/calendar': { label: 'Kalender & Zeiten', nav: ['admin-system'], anyOf: SYSTEM },
  '/system/platform/google-calendar': {
    label: 'Google-Kalender',
    nav: ['admin-system'],
    anyOf: [P.admin.system_und_schnittstellen.api_schnittstellen_ansehen],
  },
  '/system/platform/storage': { label: 'Storage (S3)', nav: ['admin-storage'], anyOf: [P.admin.storage_und_dateien.storage_ansehen] },
  '/system/compliance/gdpr': { label: 'DSGVO-Anfragen', nav: ['admin-datenschutz'], anyOf: [P.admin.datenschutz.dsgvo_anfragen_ansehen] },
  '/system/logs': {
    label: 'Protokolle',
    nav: ['admin-logs'],
    anyOf: [P.admin.protokollierung_und_logs.logs_ansehen, P.admin.protokollierung_und_logs.audit_logs_ansehen],
  },
  '/system/platform/ai': { label: 'KI', nav: ['admin-ki'], anyOf: [P.admin.ki.ki_konfiguration_ansehen] },
  '/system/cms/branding': {
    label: 'Branding',
    nav: ['admin-unternehmen'],
    anyOf: [P.admin.unternehmen_und_erscheinungsbild.erscheinungsbild_ansehen],
  },
  '/system/marketing/gutscheincodes': { label: 'Gutscheincodes', nav: ['admin-verkaufsaktionen'], anyOf: VERKAUF },
  '/system/marketing/geschenkgutscheine': { label: 'Geschenkgutscheine', nav: ['admin-verkaufsaktionen'], anyOf: VERKAUF },
  '/standort/marketing/geschenkgutscheine': { anyOf: VERKAUF },

  // Testing: Tickets dauerhaft, Testdaten nur auf dem Testsystem.
  '/testing': { label: 'Tickets', nav: ['testing-tickets'], anyOf: TICKETS },
  '/testing/:id': { anyOf: TICKETS },
  '/testing/testdaten': {
    label: 'Testdaten',
    nav: ['testing-testdaten'],
    anyOf: [P.testing.testdaten.testdaten_ansehen],
  },

  // Abnahme (docs/abnahme-plan.md)
  '/abnahme': { label: 'Abnahme', nav: ['abnahme-katalog'], anyOf: ABNAHME },
  '/abnahme/stories/:id': { anyOf: ABNAHME },
  '/abnahme/ablaeufe': { label: 'Abläufe', nav: ['abnahme-ablaeufe'], anyOf: ABNAHME },
  '/abnahme/statistik': { label: 'Statistik', nav: ['abnahme-statistik'], anyOf: ABNAHME },
  '/abnahme/report': {
    label: 'Report',
    nav: ['abnahme-report'],
    anyOf: [P.abnahme.freigabe.freigabe_ansehen],
  },
  '/abnahme/freigabe': {
    label: 'Freigabe',
    nav: ['abnahme-freigabe'],
    anyOf: [P.abnahme.freigabe.freigabe_ansehen],
  },
  '/abnahme/testlaeufe/:id': { anyOf: [P.abnahme.testlaeufe.testlaeufe_ansehen] },
  '/abnahme/testlaeufe': {
    label: 'Testläufe',
    nav: ['abnahme-testlaeufe'],
    anyOf: [P.abnahme.testlaeufe.testlaeufe_ansehen],
  },
};

/** Menüpunkte, für die es in der Oberfläche noch keine Seite gibt (leerer Bereich). */
export const NAV_NODES_WITHOUT_PAGE: readonly string[] = [];

/** Permission einer Route; unbekannte Routen verlangen eine leere Menge (niemand). */
export const accessFor = (path: string): readonly PermissionKey[] => PAGES[path]?.anyOf ?? [];

/** Sidebar-Links eines Menüpunkts in Tabellenreihenfolge. */
export function pagesForNode(
  nodeId: string
): Array<{ path: string; label: string; anyOf: readonly PermissionKey[]; gruppe?: string }> {
  return Object.entries(PAGES)
    .filter(([, page]) => page.label && page.nav?.includes(nodeId))
    .map(([path, page]) => ({ path, label: page.label as string, anyOf: page.anyOf, ...(page.gruppe ? { gruppe: page.gruppe } : {}) }));
}
